import React from "react";
import { useForm } from "react-hook-form";
import { Button, SimpleGrid, VStack } from "@chakra-ui/react";

import { useUser } from "context/user";
import { Organization } from "types/organization";
import { Callback } from "types";
import { UserRole } from "types/user";
import { Form, Input } from "components";
import { updateOrganization } from "api/webservice/organizations";
import { useToast } from "hooks";

interface Props {
  organization: Organization;
  callback: Callback;
}

type FormData = {
  name: string;
  displayName: string;
  billingAddress: string;
  rate: number;
};

const OrganizationForm: React.FC<Props> = ({
  organization,
  callback
}: Props) => {
  const { user: actor } = useUser()!;
  const { handleSubmit, errors, register, formState } = useForm<FormData>();
  const toast = useToast();

  const validateName = (value: string) => {
    if (!value) {
      return "Name is required";
    } else return true;
  };

  const validateDisplayName = (value: string) => {
    if (!value) {
      return "Organization Name is required";
    } else return true;
  };

  const validateBillingAddress = (value: string) => {
    if (!value) {
      return "Billing Address is required";
    }
    return true;
  };

  const validateBillingRate = (value: number) => {
    if (!value) {
      return "Billing Rate is required";
    }
    if (value < 0) {
      return "Billing Rate must be positive.";
    }
    return true;
  };

  const submitForm = async (data: FormData): Promise<any | null> => {
    await updateOrganization({
      ...organization,
      ...data
    })
      .then(() => callback())
      .catch(error => {
        toast({
          title: "Error updating organization",
          description: error.response.data.response.errors[0].message,
          status: "error"
        });
      });
  };

  if (!organization) return null;

  return (
    <Form defaultValues={organization} onSubmit={handleSubmit(submitForm)}>
      <VStack spacing={8}>
        <SimpleGrid columns={[1, 2]} minW={{ md: "800px" }} spacing={6}>
          <Input
            variant="outline"
            name="name"
            label="Internal Name"
            disabled={actor.privs < UserRole.Admin}
            defaultValue={organization?.name}
            required
            register={register({ validate: validateName })}
            errorMessage={errors.name?.message}
          />
          <Input
            variant="outline"
            name="displayName"
            label="Organization Name"
            defaultValue={organization?.displayName}
            required
            register={register({ validate: validateDisplayName })}
            errorMessage={errors.displayName?.message}
          />
          <Input
            variant="outline"
            type="textarea"
            name="billingAddress"
            label="Billing Address"
            defaultValue={organization?.billingAddress}
            required
            register={register({ validate: validateBillingAddress })}
          />
          <Input
            variant="outline"
            type="number"
            name="rate"
            label="Hourly Rate"
            disabled={actor.privs < UserRole.Admin}
            defaultValue={organization?.rate}
            required
            register={register({ validate: validateBillingRate })}
            errorMessage={errors.rate?.message}
          />
        </SimpleGrid>
        <Button
          disabled={formState.isSubmitting}
          type="submit"
          size="md"
          variant="blue"
        >
          Save Organization
        </Button>
      </VStack>
    </Form>
  );
};

export default OrganizationForm;
